import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";

import { ANIMATION_TIMINGS } from "../constants";
import { type ScoreChange } from "../types";

interface ScoreLineItemProps extends Omit<ScoreChange, "icon"> {
  /**
   * Whether the line item is visible (controls opacity)
   */
  isVisible: boolean;
  /**
   * Optional icon to display next to the label or value text
   */
  icon?: React.ReactNode;
  /**
   * Position of the icon relative to the line item content
   * @default "left"
   */
  iconPosition?: "left" | "right";
}

function formatValue(value: number): string {
  return value >= 0 ? `+${value}` : `${value}`;
}

function getValueColor(
  color: ScoreChange["color"]
): (theme: {
  palette: { success: { main: string }; error: { main: string }; text: { primary: string } };
}) => string {
  return (theme) => {
    if (color === "positive") {
      return theme.palette.success.main;
    }

    if (color === "negative") {
      return theme.palette.error.main;
    }

    return theme.palette.text.primary;
  };
}

export function ScoreLineItem(props: ScoreLineItemProps) {
  const { label, value, color = "default", isVisible, icon, iconPosition = "left" } = props;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${ANIMATION_TIMINGS.LINE_ITEM_TRANSITION_MS}ms ease-in-out`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {iconPosition === "left" && icon}
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {label}
        </Text>
        {iconPosition === "right" && icon}
      </Stack>
      <Text semibold variant="body2" sx={(theme) => ({ color: getValueColor(color)(theme) })}>
        {formatValue(value)}
      </Text>
    </Stack>
  );
}
